/* ---
	Custom Product Options
--- */

if (typeof CustomProductOptions !== 'function') {

	class CustomProductOptions extends HTMLElement {

		constructor() {
			super();
			this._prefix = window.KT_PREFIX || '';
			this.init();
		}

		init() {
			this.radioButtons = this.querySelectorAll('input[type="radio"][name="custom_option"]');
			this.addToCartButton = document.querySelector('[data-main-product-add-to-cart]');
			this.subscriptionProduct = this.querySelector('[data-js-subscription-product]');
			this.ADD_TO_CART = new Event('add-to-cart');

			if (this.addToCartButton && this.radioButtons.length > 0) {
				this.productForm = this.addToCartButton.closest('product-form');
				this.addToCartButton.addEventListener('click', this.onAddToCart.bind(this));

				this.radioButtons.forEach(radio => {
					radio.addEventListener('change', this.onRadioChange.bind(this));
				});

				// Find main quantity input
				this.mainQuantityInput = null;
				if (this.productForm) {
					this.mainQuantityInput = this.productForm.querySelector('input[name="quantity"]');
				}
				if (!this.mainQuantityInput) {
					this.mainQuantityInput = document.querySelector('input[name="quantity"]');
				}

				if (this.mainQuantityInput) {
					this.mainQuantityInput.addEventListener('change', this.onMainQuantityChange.bind(this));
					// Also listen to input for intermediate updates if desired, but change is standard for completion
					this.mainQuantityInput.addEventListener('input', this.onMainQuantityChange.bind(this));

					// Initial sync: set main quantity to currently selected radio value
					const checkedRadio = Array.from(this.radioButtons).find(radio => radio.checked);
					if (checkedRadio) {
						this.mainQuantityInput.value = checkedRadio.value;
						// Dispatch event just in case
						this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
					}
				}
			}
		}

		onMainQuantityChange(e) {
			const qty = parseInt(e.target.value);
			if (isNaN(qty)) return;

			// Find matching radio
			const matchingRadio = Array.from(this.radioButtons).find(radio => parseInt(radio.value) === qty);

			if (matchingRadio) {
				matchingRadio.checked = true;
				this.updateVisualState(matchingRadio);
			}
		}

		updateVisualState(selectedRadio) {
			const customOptions = selectedRadio.closest('cstm-options');

			customOptions.querySelectorAll('.product-custom-variants__item').forEach(item => {
				item.classList.remove('selected');
			});

			selectedRadio.closest('.product-custom-variants__item').classList.add('selected');
		}

		onRadioChange(e) {
			const selectedRadio = e.target;
			this.updateVisualState(selectedRadio);

			if (this.mainQuantityInput) {
				this.mainQuantityInput.value = selectedRadio.value;
				// Dispatch change/input event so other things listening to quantity (like global theme scripts) know it changed
				this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
			}
		}

		onAddToCart(e) {
			const checkedRadio = Array.from(this.radioButtons).find(radio => radio.checked);

			if (!checkedRadio) {
				e.preventDefault();
				e.stopPropagation();

				this.showError();

				this.scrollIntoView({ behavior: 'smooth', block: 'center' });

				return false;
			}

			e.preventDefault();
			e.stopPropagation();

			this.clearError();

			let selectedQuantity = parseInt(checkedRadio.value);

			if (this.mainQuantityInput) {
				const mainQty = parseInt(this.mainQuantityInput.value);
				if (!isNaN(mainQty)) {
					selectedQuantity = Math.max(selectedQuantity, mainQty);
				}
			}
			let subscriptionProduct = null;
			let giftProduct = null;

			if (this.subscriptionProduct && this.subscriptionProduct.textContent.trim()) {
				try {
					subscriptionProduct = JSON.parse(this.subscriptionProduct.textContent);
				} catch (e) {
					console.warn('Failed to parse subscription product JSON:', e);
				}
			}

			// Get gift product for selected option
			const selectedOption = checkedRadio.closest('.product-custom-variants__item');
			const giftScript = selectedOption.querySelector('[data-js-gift-product]');

			if (giftScript && giftScript.textContent.trim()) {
				try {
					giftProduct = JSON.parse(giftScript.textContent);
				} catch (e) {
					console.warn('Failed to parse gift product JSON:', e);
				}
			}

			this.addToCart(selectedQuantity, subscriptionProduct, giftProduct);
		}

		async addToCart(quantity, subscriptionProduct, giftProduct) {
			const addToCartButton = this.addToCartButton;
			const productForm = this.productForm;
			const form = productForm.querySelector('form');

			addToCartButton.classList.add('working');

			try {
				const mainProductData = this.serializeForm(form);
				mainProductData.set('quantity', quantity.toString());

				const response1 = await fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
					body: mainProductData,
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
						'X-Requested-With': 'XMLHttpRequest'
					},
					method: 'POST'
				});

				const result1 = await response1.json();

				if (result1.status === 422) {
					throw new Error(result1.description || result1.message);
				}

				// Handle gift product if present
				let giftPlanId = null;
				let shouldReplaceSubscription = false;

				if (giftProduct && giftProduct.product && giftProduct.product.id) {

					const cartResponse = await fetch(`${KROWN.settings.routes.cart_url}.js`);
					const cart = await cartResponse.json();

					// Check if gift matches subscription product
					if (subscriptionProduct && subscriptionProduct.id === giftProduct.product.id) {
						shouldReplaceSubscription = true;

						// Find the special trial plan "Annual Subscription (+1 month)"
						if (giftProduct.product.selling_plan_groups && giftProduct.product.selling_plan_groups.length > 0) {
							const firstGroup = giftProduct.product.selling_plan_groups[0];
							if (firstGroup.selling_plans && firstGroup.selling_plans.length > 0) {
								const trialPlan = firstGroup.selling_plans.find(plan => {
									const match = plan.name?.includes('(+1 month)');
									return match;
								});
								if (trialPlan) {
									giftPlanId = trialPlan.id;
								}
							}
						}
					}

					// Check if gift is already in cart for this parent product
					const parentProductId = this.productForm.dataset.productId;

					const giftAlreadyInCart = cart.items?.some(item => {
						const match = item.product_id === giftProduct.product.id &&
							item.properties &&
							item.properties['__Parent Product ID'] &&
							item.properties['__Parent Product ID'].toString() === parentProductId.toString();
						return match;
					});

					if (!giftAlreadyInCart) {
						const giftData = new URLSearchParams();
						giftData.set('id', giftProduct.product.variants[0].id.toString());
						giftData.set('quantity', '1');

						// Add selling plan if gift has trial subscription
						if (giftPlanId) {
							giftData.set('selling_plan', giftPlanId.toString());
						}

						// Add parent product ID to properties for linking gift to parent
						if (parentProductId) {
							giftData.set('properties[__Parent Product ID]', parentProductId.toString());
						}

						const giftResponse = await fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
							body: giftData,
							headers: {
								'Content-Type': 'application/x-www-form-urlencoded',
								'X-Requested-With': 'XMLHttpRequest'
							},
							method: 'POST'
						});

						const giftResult = await giftResponse.json();

						if (giftResult.status === 422) {
							console.warn('Gift product could not be added:', giftResult.description || giftResult.message);
						}
					}
				}

				if (subscriptionProduct && subscriptionProduct.variants && subscriptionProduct.variants.length > 0) {
					let sellingPlanId = null;
					if (subscriptionProduct.selling_plan_groups && subscriptionProduct.selling_plan_groups.length > 0) {
						const firstGroup = subscriptionProduct.selling_plan_groups[0];
						if (firstGroup.selling_plans && firstGroup.selling_plans.length > 0) {
							const yearlyPlan = firstGroup.selling_plans.find(plan => {
								const match = plan.billing_policy?.recurring?.interval === 'YEAR' ||
									plan.name?.toLowerCase().includes('year') ||
									plan.name?.toLowerCase().includes('annual');
								return match;
							});
							if (yearlyPlan) {
								sellingPlanId = yearlyPlan.id;
							} else {
								sellingPlanId = firstGroup.selling_plans[0].id;
							}
						}
					}

					if (sellingPlanId) {
						const cartResponse = await fetch(`${KROWN.settings.routes.cart_url}.js`);
						const cart = await cartResponse.json();

						// Skip adding regular subscription if we're replacing it with gift trial
						if (!shouldReplaceSubscription) {

							const subscriptionAlreadyInCart = cart.items?.some(item => {
								const match = item.product_id === subscriptionProduct.id &&
									item.selling_plan_allocation?.selling_plan?.id === sellingPlanId;
								return match;
							});

							if (!subscriptionAlreadyInCart) {
								const subscriptionData = new URLSearchParams();
								subscriptionData.set('id', subscriptionProduct.variants[0].id.toString());
								subscriptionData.set('quantity', '1');
								subscriptionData.set('selling_plan', sellingPlanId.toString());

								// Add parent product ID to properties for linking subscription to parent
								const parentProductId = this.productForm.dataset.productId;
								if (parentProductId) {
									subscriptionData.set('properties[__Parent Product ID]', parentProductId.toString());
								}

								const response2 = await fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
									body: subscriptionData,
									headers: {
										'Content-Type': 'application/x-www-form-urlencoded',
										'X-Requested-With': 'XMLHttpRequest'
									},
									method: 'POST'
								});

								const result2 = await response2.json();

								if (result2.status === 422) {
									console.warn('Subscription product could not be added:', result2.description || result2.message);
								}
							}
						}
					}
				}

				let cartResponse;
				if (KROWN.settings.cart_action === 'popup') {
					cartResponse = await fetch('?section_id=helper-cart-popup');
				} else {
					cartResponse = await fetch('?section_id=helper-cart');
				}

				if (cartResponse) {
					const cartText = await cartResponse.text();
					const sectionInnerHTML = new DOMParser().parseFromString(cartText, 'text/html');

					if (KROWN.settings.cart_action === 'popup') {
						if (document.querySelector('[data-js-site-cart-popup-content]')) {
							document.querySelector('[data-js-site-cart-popup-content]').innerHTML = sectionInnerHTML.querySelector('.shopify-section').innerHTML;
						}
					} else {
						const cartFormInnerHTML = sectionInnerHTML.getElementById('AjaxCartForm').innerHTML;
						const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal').innerHTML;

						if (document.getElementById('AjaxCartForm')) {
							const cartItems = document.getElementById('AjaxCartForm');
							cartItems.innerHTML = cartFormInnerHTML;
							cartItems.ajaxifyCartItems();
							document.getElementById('AjaxCartSubtotal').innerHTML = cartSubtotalInnerHTML;
						}
					}

					document.querySelectorAll('[data-header-cart-count]').forEach(elm => {
						elm.textContent = sectionInnerHTML.querySelector('[data-cart-count]').textContent;
					});
					document.querySelectorAll('[data-header-cart-total]').forEach(elm => {
						elm.textContent = sectionInnerHTML.querySelector('[data-cart-total]').textContent;
					});

					if (this.productForm) {
						this.productForm.dispatchEvent(this.ADD_TO_CART);
					} else {
						document.dispatchEvent(this.ADD_TO_CART);
					}

					if (KROWN.settings.cart_action === 'overlay') {
						const cartSidebar = document.getElementById('site-cart-sidebar');
						if (cartSidebar && typeof cartSidebar.show === 'function') {
							cartSidebar.show();
						}
					}

					if (document.getElementById('screen-reader-info')) {
						document.getElementById('screen-reader-info').innerText = `${KROWN.settings.locales.cart_announcement}`;
						setTimeout(() => {
							document.getElementById('screen-reader-info').innerText = '';
						}, 1000);
					}
				}

			} catch (error) {
				const alert = document.createElement('span');
				alert.className = `${this._prefix}alert ${this._prefix}alert--error`;
				alert.innerHTML = error.message;
				alert.style.marginTop = '1em';
				alert.style.marginBottom = '0';
				alert.id = 'product-page-form-error-cart-alert';

				if (document.getElementById('product-page-form-error-cart-alert')) {
					document.getElementById('product-page-form-error-cart-alert').remove();
				}

				form.parentElement.append(alert);
			} finally {
				addToCartButton.classList.remove('working');
			}
		}

		serializeForm(form) {
			const data = new URLSearchParams();
			Array.from(form.elements).forEach(field => {
				if (!field.name || field.disabled ||
					['file', 'reset', 'submit', 'button'].includes(field.type) ||
					(['checkbox', 'radio'].includes(field.type) && !field.checked)) {
					return;
				}
				if (field.type === 'select-multiple') {
					Array.from(field.options).forEach(option => {
						if (option.selected) {
							data.append(field.name, option.value);
						}
					});
					return;
				}
				data.append(field.name, field.value);
			});
			return data;
		}

		showError(message) {
			this.clearError();

			const errorElement = document.createElement('div');
			errorElement.className = `${this._prefix}alert ${this._prefix}alert--error`;
			errorElement.textContent = message || this.dataset.customOptionRequired;
			errorElement.style.marginTop = '1em';
			errorElement.style.marginBottom = '1em';
			errorElement.id = 'custom-options-error';

			this.parentNode.insertBefore(errorElement, this);

			errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}

		clearError() {
			const existingError = document.getElementById('custom-options-error');
			if (existingError) {
				existingError.remove();
			}
		}
	}

	if (typeof customElements.get('cstm-options') === 'undefined') {
		customElements.define('cstm-options', CustomProductOptions);
	}
}